import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { TwoFactorSetup } from '@/components/TwoFactorSetup';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, Shield, Lock, Key } from 'lucide-react';
import { twoFactorService } from '@/services/twoFactorService';
import { useToast } from '@/components/ui/use-toast';

const Security = () => {
  const { user, isAuthenticated } = useAuth();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [twoFactorEnabled, setTwoFactorEnabled] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isAuthenticated) {
      fetchTwoFactorStatus();
    }
  }, [isAuthenticated]);

  const fetchTwoFactorStatus = async () => {
    try {
      const status = await twoFactorService.getStatus();
      setTwoFactorEnabled(status.enabled);
    } catch (err: unknown) {
      setError('Error al cargar el estado de seguridad');
      console.error('Error fetching 2FA status:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleTwoFactorStatusChange = (enabled: boolean) => {
    setTwoFactorEnabled(enabled);
  };

  if (!isAuthenticated) {
    return (
      <div className="container mx-auto px-4 py-8">
        <Alert>
          <Shield className="h-4 w-4" />
          <AlertDescription>
            Debes iniciar sesión para acceder a la configuración de seguridad.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="flex items-center justify-center min-h-[400px]">
          <Loader2 className="h-8 w-8 animate-spin" />
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8 max-w-4xl">
      <div className="mb-8">
        <h1 className="text-3xl font-bold mb-2">Configuración de Seguridad</h1>
        <p className="text-muted-foreground">
          Administra la seguridad de tu cuenta de {user?.name}
        </p>
      </div>

      {error && (
        <Alert variant="destructive" className="mb-6">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <div className="space-y-6">
        {/* Información de la cuenta */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Lock className="h-5 w-5" />
              Información de la Cuenta
            </CardTitle>
            <CardDescription>
              Detalles básicos de tu cuenta
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-muted-foreground">Nombre</label>
                <p className="text-sm">{user?.name}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-muted-foreground">Email</label>
                <p className="text-sm">{user?.email}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-muted-foreground">Rol</label>
                <p className="text-sm capitalize">{user?.role}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-muted-foreground">Registro</label>
                <p className="text-sm">
                  {user?.createdAt ? new Date(user.createdAt).toLocaleDateString() : 'N/A'}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Configuración de 2FA */}
        <TwoFactorSetup
          isEnabled={twoFactorEnabled}
          onStatusChange={handleTwoFactorStatusChange}
        />

        {/* Recomendaciones de seguridad */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Key className="h-5 w-5" />
              Recomendaciones de Seguridad
            </CardTitle>
            <CardDescription>
              Consejos para mantener tu cuenta segura
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ul className="space-y-3 text-sm">
              <li className="flex items-start gap-2">
                <span className="text-green-500 mt-1">•</span>
                <span>Usa una contraseña fuerte y única para esta cuenta</span>
              </li>
              <li className="flex items-start gap-2">
                <span className={twoFactorEnabled ? "text-green-500" : "text-orange-500"}>•</span>
                <span>
                  {twoFactorEnabled 
                    ? "✓ Tienes habilitada la autenticación de dos factores"
                    : "Habilita la autenticación de dos factores para mayor seguridad"
                  }
                </span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-blue-500 mt-1">•</span>
                <span>Cierra sesión en dispositivos que no uses frecuentemente</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-blue-500 mt-1">•</span>
                <span>Mantén actualizada tu aplicación de autenticación</span>
              </li>
            </ul>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Security;